import React from 'react';
import { useTheme, THEME_LABELS, THEME_DESCRIPTIONS, type Theme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

/** Mini UI preview: sidebar strip + surface + accent chips */
const THEME_PREVIEW: Record<
  Theme,
  { shell: string; panel: string; accent: string; topbar: string; text: string }
> = {
  light: {
    shell: 'bg-[#0a1424]',
    panel: 'bg-slate-50',
    accent: 'bg-[#6b8fb8]',
    topbar: 'bg-white',
    text: 'bg-slate-300',
  },
  dark: {
    shell: 'bg-[#060a14]',
    panel: 'bg-[#0e1524]',
    accent: 'bg-[#6b8fb8]',
    topbar: 'bg-[#121a2b]',
    text: 'bg-slate-600',
  },
  ocean: {
    shell: 'bg-[#0a1628]',
    panel: 'bg-sky-50',
    accent: 'bg-sky-500',
    topbar: 'bg-white',
    text: 'bg-sky-200',
  },
  harbor: {
    shell: 'bg-[#042022]',
    panel: 'bg-teal-50',
    accent: 'bg-teal-500',
    topbar: 'bg-white',
    text: 'bg-teal-200',
  },
  slate: {
    shell: 'bg-zinc-950',
    panel: 'bg-zinc-100',
    accent: 'bg-zinc-600',
    topbar: 'bg-white',
    text: 'bg-zinc-300',
  },
};

function ThemeCardPreview({ themeId }: { themeId: Theme }) {
  const p = THEME_PREVIEW[themeId];
  return (
    <div className="relative flex h-28 w-full overflow-hidden rounded-xl border border-black/10 shadow-inner dark:border-white/10">
      <div className={cn('flex w-[28%] flex-col gap-1.5 p-2', p.shell)}>
        <div className={cn('h-1.5 w-full rounded-full opacity-90', p.accent)} />
        <div className="mt-1 h-1 w-4/5 rounded-full bg-white/25" />
        <div className="h-1 w-3/5 rounded-full bg-white/15" />
        <div className="h-1 w-4/5 rounded-full bg-white/15" />
        <div className={cn('mt-auto h-1.5 w-full rounded-full opacity-70', p.accent)} />
      </div>
      <div className={cn('flex min-w-0 flex-1 flex-col', p.panel)}>
        <div
          className={cn(
            'flex h-6 items-center gap-1.5 border-b border-black/5 px-2 dark:border-white/5',
            p.topbar,
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', p.accent)} />
          <span className={cn('h-1 w-10 rounded-full opacity-50', p.text)} />
          <span className="ml-auto h-3 w-3 rounded-full bg-black/5 dark:bg-white/10" />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-2.5">
          <div className="flex items-center justify-between">
            <span className={cn('h-1.5 w-16 rounded-full opacity-60', p.text)} />
            <span className={cn('h-4 w-10 rounded-md opacity-90', p.accent)} />
          </div>
          <div className="mt-0.5 flex-1 rounded-lg border border-black/5 bg-white/60 p-2 dark:border-white/5 dark:bg-white/5">
            <div className={cn('mb-1.5 h-1 w-3/4 rounded-full opacity-40', p.text)} />
            <div className={cn('mb-1 h-1 w-full rounded-full opacity-25', p.text)} />
            <div className={cn('h-1 w-2/3 rounded-full opacity-25', p.text)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export type ThemePickerProps = {
  value: Theme;
  onChange: (theme: Theme) => void;
  className?: string;
};

/**
 * Visual theme picker for Account settings → Appearance.
 */
export function ThemePicker({ value, onChange, className }: ThemePickerProps) {
  const { colors, availableThemes } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}
    >
      {availableThemes.map((themeId) => {
        const selected = value === themeId;
        return (
          <button
            key={themeId}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(themeId)}
            className={cn(
              'group relative rounded-2xl border-2 p-3 text-left transition-all',
              selected
                ? cn(colors.primaryBorderAll, 'bg-slate-50/90 shadow-sm dark:bg-white/[0.04]')
                : cn(colors.border, 'border', colors.interactiveHover),
            )}
          >
            <ThemeCardPreview themeId={themeId} />
            <div className="mt-3 flex items-start gap-2.5">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  selected
                    ? cn(colors.primary, colors.primaryText, 'border-transparent')
                    : cn(colors.border, 'bg-transparent'),
                )}
                aria-hidden
              >
                {selected && (
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm font-semibold', colors.text)}>
                  {THEME_LABELS[themeId]}
                </span>
                <span className={cn('mt-0.5 block text-xs leading-snug', colors.textMuted)}>
                  {THEME_DESCRIPTIONS[themeId]}
                </span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default ThemePicker;
